import { ReactNode, useContext } from "react";
import { useParams } from "react-router-dom";

import { Anchor, Card, Group, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { TextWrapper } from "@/components/TextWrapper";
import { PatientContext } from "@/contexts/patient";
import { DevelopmentFeatureFlags, useFeature } from "@/feature-flags";
import {
  buildOverflowLabel,
  CLIENT_FLAGS_SECTION_TITLE,
  ClientFlagRow,
  ClientFlagsDrawer,
  ClientFlagsEmptyRow,
  ClientFlagVisibilityInfo,
  MANAGE_CLIENT_FLAGS_LABEL,
  summarizeClientFlags,
  useClientFlags,
} from "@/flags";

/**
 * The rows of the `Flags` section, mounted only once the feature gate has let them through.
 *
 * Split from the gate below so the chart read starts with the section rather than with every
 * client profile: the hook's effect runs as soon as this is mounted.
 */
function ClientFlagsSection() {
  const { clientId } = useParams() as { clientId: string };
  const { flags, isLoading, errorMessage, isRefused } = useClientFlags(clientId);
  const { patient } = useContext(PatientContext);
  const [isDrawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // A role that may not read flags opens the chart legitimately, so it gets no section at all.
  if (isLoading || isRefused) return null;

  const clientFirstName = patient?.name?.[0]?.given?.[0] ?? "";

  const { visible, overflowCount, overflowByPriority } = summarizeClientFlags(flags);

  // The empty row carries its own `+ Add Flags`, so `Manage` would only repeat it.
  const isEmpty = !errorMessage && !flags.length;

  let body: ReactNode;
  if (errorMessage) {
    body = (
      <TextWrapper size="sm" c="gray.9">
        {errorMessage}
      </TextWrapper>
    );
  } else if (isEmpty) {
    body = <ClientFlagsEmptyRow onAdd={openDrawer} />;
  } else {
    body = (
      <Stack gap="xs">
        {visible.map((flag) => (
          <ClientFlagRow flag={flag} key={flag.id} />
        ))}
        {overflowCount > 0 && (
          <TextWrapper size="sm" c="gray.6" data-testid="client-flags-overflow">
            {buildOverflowLabel(overflowCount, overflowByPriority)}
          </TextWrapper>
        )}
      </Stack>
    );
  }

  return (
    <Card c="dark.9" bg="white" data-testid="client-flags-section">
      <Group mb="xs" justify="space-between">
        <Group gap="xs">
          <TextWrapper fz="md" variant="bold">
            {CLIENT_FLAGS_SECTION_TITLE}
          </TextWrapper>
          <ClientFlagVisibilityInfo />
        </Group>
        {!isEmpty && (
          <Anchor
            fz="sm"
            fw={600}
            type="button"
            c="burgundy.5"
            component="button"
            underline="always"
            onClick={openDrawer}
            data-testid="client-flags-manage"
          >
            {MANAGE_CLIENT_FLAGS_LABEL}
          </Anchor>
        )}
      </Group>
      {body}
      <ClientFlagsDrawer
        clientId={clientId}
        onClose={closeDrawer}
        opened={isDrawerOpened}
        clientFirstName={clientFirstName}
      />
    </Card>
  );
}

/**
 * The `Flags` section of the client info box.
 *
 * Always expanded — no chevron and no count badge, unlike `Problems` and `Allergies`. A client with
 * no flags gets one `No flags added` row in place of the list, whose `+ Add Flags` takes the place
 * of `Manage`.
 *
 * `Manage` opens the drawer that lists and applies flags. A save or removal there re-reads this
 * section through the shared `clientFlagsChanged` signal.
 */
export default function ClientFlags() {
  const isFlagsFeatureEnabled = useFeature(DevelopmentFeatureFlags.Flags);

  // Gated alongside every other flags surface, so a practice without the feature sees no trace of
  // it on the chart and makes no read for it either.
  if (!isFlagsFeatureEnabled) return null;

  return <ClientFlagsSection />;
}
